import type { AgGridPriceProps, Security } from '@talos/kyoko';
import { PositionAssetTypeEnum } from '@talos/kyoko';
import {
  formatGovernmentBondPriceIn32nds,
  GOVERNMENT_BOND_PRICE_DISPLAY_INCREMENT,
  isGovernmentBondSecurity,
} from '../governmentBondPrice';

export type PmsGovernmentBondRow = {
  gridData: {
    Asset: string;
    AssetType: keyof typeof PositionAssetTypeEnum;
  };
};

export function createSecurityAwareGovernmentBondMarkPriceParams<T extends object>(
  enableGovernmentBond32nds: boolean,
  securitiesBySymbol: ReadonlyMap<string, Security> | undefined,
  getAsset: (data: T | undefined) => string | undefined,
  getAssetType?: (data: T | undefined) => PositionAssetTypeEnum | undefined
): Pick<AgGridPriceProps<T>, 'increment' | 'transformPriceForDisplay' | 'showCurrency'> {
  return getGovernmentBondMarkPriceParams(enableGovernmentBond32nds, data => {
    const asset = getAsset(data);
    if (asset && isGovernmentBondSecurity(securitiesBySymbol?.get(asset))) {
      return PositionAssetTypeEnum.GovernmentBond;
    }
    return getAssetType?.(data);
  });
}

export function createPmsGovernmentBondMarkPriceParams<T extends PmsGovernmentBondRow>(
  enableGovernmentBond32nds: boolean,
  securitiesBySymbol: ReadonlyMap<string, Security> | undefined
): Pick<AgGridPriceProps<T>, 'increment' | 'transformPriceForDisplay' | 'showCurrency'> {
  return createSecurityAwareGovernmentBondMarkPriceParams(
    enableGovernmentBond32nds,
    securitiesBySymbol,
    data => data?.gridData.Asset,
    data => (data ? PositionAssetTypeEnum[data.gridData.AssetType] : undefined)
  );
}

export function getGovernmentBondMarkPriceParams<T extends object>(
  enableGovernmentBond32nds: boolean,
  getAssetType: (data: T | undefined) => PositionAssetTypeEnum | undefined
): Partial<{
  increment: Exclude<AgGridPriceProps<T>['increment'], string>;
  transformPriceForDisplay: NonNullable<AgGridPriceProps<T>['transformPriceForDisplay']>;
  showCurrency: Exclude<AgGridPriceProps<T>['showCurrency'], boolean>;
}> {
  if (!enableGovernmentBond32nds) {
    return {};
  }

  const isGovernmentBond = (data: T | undefined) => getAssetType(data) === PositionAssetTypeEnum.GovernmentBond;

  return {
    increment: data => (isGovernmentBond(data) ? GOVERNMENT_BOND_PRICE_DISPLAY_INCREMENT : undefined),
    transformPriceForDisplay: (price, data) =>
      isGovernmentBond(data) ? formatGovernmentBondPriceIn32nds(price) : price,
    showCurrency: data => !isGovernmentBond(data),
  };
}
